import React from 'react';
import { observer } from "mobx-react";
import Griddle, { plugins, RowDefinition, ColumnDefinition } from 'griddle-react';
import { connect } from 'react-redux';
import { FaCaretDown, FaCaretUp } from "react-icons/fa";
import colorbrewer from 'colorbrewer';
import ScrollContainer from 'react-indiana-drag-scroll'

import CountyImage from './CountyImage'
import LoadingSpinner from './LoadingSpinner';
import Link from './Link'

import {
  COVID_14_DAY_RATE_CATEGORIES, AQI_CATEGORIES, RED_GRADIENT_HEX,
  generateCategories, getCategory
} from '../lib/categories'

import {
  countyTableDataStore, regionCountyDataStore
} from '../store/apiStores'

import './CountyReportTable.css'

// e.g. report data:
//  {
//    "active": 67,
//    "active_difference": 4,
//    "confirmed": 67,
//    "confirmed_difference": 4,
//    "confirmed_last_7": 6720,
//    "confirmed_last_7_per_100k": 67,
//    "county": "Abbeville",
//    "county_uid": 2317,
//    "deaths": 0,
//    "deaths_difference": 0,
//    "five_day_doubling_rate": 31.41,
//    "id": 499810,
//    "imported_at": "Mon, 15 Jun 2020 16:55:11 GMT",
//    "max_3hr_pm25_avg": null,
//    "max_pm25": null,
//    "mean_pm25": null,
//    "min_pm25": null,
//    "pct_confirmed": 0.003,
//    "population": 24527,
//    "rate_active": 27.3,
//    "rate_confirmed": 27.3,
//    "rate_confirmed_difference": 1.6,
//    "rate_death": 0.0,
//    "rate_recovered": 0.0,
//    "recovered": 0,
//    "recovered_difference": 0,
//    "report_date": "Sun, 14 Jun 2020 00:00:00 GMT",
//    "single_day_doubling_rate": 11.26,
//    "state": "South Carolina",
//    "state_uid": 45
//  }

const rowDataSelector = (state, { griddleKey }) => {
  return state
    .get('data')
    .find(rowMap => rowMap.get('griddleKey') === griddleKey)
    .toJSON();
};

const enhancedWithRowData = connect((state, props) => {
  return {
    // rowData will be available in custom components
    rowData: rowDataSelector(state, props)
  };
});


// Layout

function getLayoutComponent(includeFilter, includePagination) {
  return (args) => (
    <div>
      {includeFilter && <args.Filter />}
      <ScrollContainer className="table-element-wrapper">
        <args.Table />
      </ScrollContainer>
      {includePagination && <args.Pagination />}
    </div>
  );
}


// Custom Headings

// Store column id <=> titles relationship here to enable
// dynamic changing of column classes on sort
const columnTitles = {
  "county": "County",
  "state": "State",
  "population": "Population",
  "confirmed_last_14_per_100k": "14d Rate",
  "confirmed": "Total",
  "confirmed_difference": "New Cases",
  "deaths": "Deaths",
  "deaths_difference": "New Deaths",
  "recovered": "Recovered",
  "recovered_difference": "New Recovered",
  "active": "Active",
  "active_difference": "New Active",
  "rate_confirmed": "Rate",
  "five_day_doubling_rate": "Doubling Rate",
  "rate_death": "Fatality Rate",
  "rate_recovered": "Recovered Rate",
  "rate_active": "Active Rate",
  "max_pm25": "24HR Avg Max",
  "mean_pm25": "24HR Avg Mean",
  "max_3hr_pm25_avg": "Highest 3HRs",
  "min_pm25": "24HR Avg Min",
  "exposure": "PM2.5 Exposure"
}
const idsByTitle = Object.keys(columnTitles).reduce((r, e) => {
  r[columnTitles[e]] = e;
  return r;
}, {})

const DefaultHeading = ({group, title, unit, groupSubTitle}) => (
  <div id={`${idsByTitle[title]}-column-heading`} className="column-heading">
    <div className="header-group-wrapper">
      <div className="header-group left-align">{group}
      {groupSubTitle && (
        <div className="header-group-sub-title">{groupSubTitle}</div>
      )}
      </div>
    </div>
    <div className="header-title">{title}<span>{}</span></div>
    <div className="header-unit">{unit}</div>
  </div>
)
const CountyHeading = ({title}) => <DefaultHeading group="Location" title={title} />
const FourteenDayConfirmedHeading = ({title}) => <DefaultHeading group="COVID-19" title={title} unit="(conf/100k)"/>
const TotalCasesHeading = ({title}) => <DefaultHeading title={title} unit="(confirmed)"/>
const RateHeading = ({title}) => <DefaultHeading title={title} unit="(conf/10k)" />
const DoublingRateHeading = ({title}) => <DefaultHeading title={title} unit="(days; 5d avg)" />
const PM25GroupHeading = ({title}) => <DefaultHeading group="PM2.5 Observed" groupSubTitle="All Area Monitors" title={title} unit="(ug/m^3)" />
const PM25Heading = ({title}) => <DefaultHeading title={title} unit="(ug/m^3)" />
const PM253HrHeading = ({title}) => <DefaultHeading title={title} unit="(in past 24, ug/m^3)" />
const ExposureGroupHeading = ({title}) => <DefaultHeading group="PM2.5 Exposure" title={title} unit="(M pop*ug/m^3)" />

// Custom columns

const RoundNumberColumn = ({value}) => (
  <span>{value && Math.round(value).toLocaleString()}</span>
)
const RoundNumberToTenthsDigitColumn = ({value}) => (
  <span>{value && value.toFixed(1).toLocaleString()}</span>
)


const ColorCodedColumn = ({value, categories, children}) => {
  let style = {
    padding: '1rem' // to make up fo rthe padding removed from containing cell
  }
  if (value !== null) {
    let r = getCategory(categories, value);
    if (r) {
      style.backgroundColor = r.category.color;
    }
  }

  return (
    <div style={style}>
      {children}
    </div>
  )
}

const colorCodeCat = {
  confirmed: generateCategories(5000, RED_GRADIENT_HEX),
  confirmed_difference: generateCategories(1000, RED_GRADIENT_HEX),
  deaths: generateCategories(1000, RED_GRADIENT_HEX),
  deaths_difference: generateCategories(500, RED_GRADIENT_HEX),
  active: generateCategories(5000, RED_GRADIENT_HEX),
  active_difference: generateCategories(1000, RED_GRADIENT_HEX),
  rate_confirmed: generateCategories(1000, RED_GRADIENT_HEX),
  five_day_doubling_rate: generateCategories(
    28, RED_GRADIENT_HEX, {reverse: true}),
  rate_death: generateCategories(100, RED_GRADIENT_HEX),
  rate_active: generateCategories(1000, RED_GRADIENT_HEX),
  exposure: generateCategories(200, RED_GRADIENT_HEX),
}
const FourteenDayConfirmedColumn = ({value}) => <ColorCodedColumn value={value} categories={COVID_14_DAY_RATE_CATEGORIES}>{value && value.toLocaleString()}</ColorCodedColumn>
const ConfirmedColumn = ({value}) => <ColorCodedColumn value={value} categories={colorCodeCat.confirmed}>{value && value.toLocaleString()}</ColorCodedColumn>
const NewConfirmedColumn = ({value}) => <ColorCodedColumn value={value} categories={colorCodeCat.confirmed_difference}>{value && value.toLocaleString()}</ColorCodedColumn>
const DeathsColumn = ({value}) => <ColorCodedColumn value={value} categories={colorCodeCat.deaths}>{value && value.toLocaleString()}</ColorCodedColumn>
const NewDeathsColumn = ({value}) => <ColorCodedColumn value={value} categories={colorCodeCat.deaths_difference}>{value && value.toLocaleString()}</ColorCodedColumn>
const ActiveColumn = ({value}) => <ColorCodedColumn value={value} categories={colorCodeCat.active}>{value && value.toLocaleString()}</ColorCodedColumn>
const NewActiveColumn = ({value}) => <ColorCodedColumn value={value} categories={colorCodeCat.active_difference}>{value && value.toLocaleString()}</ColorCodedColumn>
const ConfirmedRateColumn = ({value}) => <ColorCodedColumn value={value} categories={colorCodeCat.rate_confirmed}><RoundNumberToTenthsDigitColumn value={value} /></ColorCodedColumn>
const DoublingRateColumn = ({value}) => <ColorCodedColumn value={value} categories={colorCodeCat.five_day_doubling_rate}><RoundNumberToTenthsDigitColumn value={value} /></ColorCodedColumn>
const DeathRateColumn = ({value}) => <ColorCodedColumn value={value} categories={colorCodeCat.rate_death}><RoundNumberToTenthsDigitColumn value={value} /></ColorCodedColumn>
const ActiveRateColumn = ({value}) => <ColorCodedColumn value={value} categories={colorCodeCat.rate_active}><RoundNumberToTenthsDigitColumn value={value} /></ColorCodedColumn>
const ExposureColumn = ({value}) => <ColorCodedColumn value={value} categories={colorCodeCat.exposure}><RoundNumberToTenthsDigitColumn value={value} /></ColorCodedColumn>

const PM25Column = ({value}) => (
  <ColorCodedColumn value={value} categories={AQI_CATEGORIES}>
    <RoundNumberColumn value={value} />
  </ColorCodedColumn>
)

function sortDescendingFunc(data, column) {
  return data.sort((a, b) => {
    return b.get(column) - a.get(column)
  })
}
function sortAscendingFunc(data, column) {
  return data.sort((a, b) => {
    if (a.get(column) === null & b.get(column) !== null) {
      return 1
    } else if (a.get(column) !== null & b.get(column) === null) {
      return -1
    }
    return a.get(column) - b.get(column)
  })
}

export default observer(function CountyReportTable(props){
    let dataStore = (props.useRegionCountyDataStore)
      ? (regionCountyDataStore) : (countyTableDataStore);

    if (!dataStore.data) {
      return (
        <LoadingSpinner />
      )
    }

    const StateColumn = (props.noStateCountyLinks) ? (
        ({value}) => (
          <span>{value}</span>
        )
      ) : (
        ({value}) => (
          <Link to={`/dashboard/${value.replace(/ /g, '-')}`}>{value}</Link>
        )
      );
    const CountyColumn =  (props.noStateCountyLinks) ? (
        ({value, griddleKey, rowData}) => (
          <span>
            <CountyImage county={value} state={rowData.state} pixelSize={30} />{value}
          </span>
        )
      ) : (
        ({value, griddleKey, rowData}) => (
          <Link to={`/dashboard/${rowData.state.replace(/ /g, '-')}/${value.replace(/ /g, '-')}`}>
            <CountyImage county={value} state={rowData.state} pixelSize={30} />{value}
          </Link>
        )
      );


    const initialSortColumn = 'exposure';
    const initialSortFunc = sortDescendingFunc;
    let lastSortColumn = null;
    let lastSortFunc = null;

    function sortDescendingOnly(data, column, sortAscending) {
      lastSortColumn = column;
      lastSortFunc = sortDescendingFunc;
      return sortDescendingFunc(data, column);
    }
    function sortAscendingOnly(data, column, sortAscending) {
      lastSortColumn = column;
      lastSortFunc = sortAscendingFunc;
      return sortAscendingFunc(data, column);
    }
    function noSort(data, column, sortAscending) {
      // fake not sorting by sorting by previous sort field.
      // Just returning data results in unexpected sort.
      let lastColumn = lastSortColumn || initialSortColumn;
      let lastFunc = lastSortFunc || initialSortFunc;
      console.log("no sort - resorting by "+lastColumn)
      return lastFunc(data, lastColumn)
    }
    const sortProperties = [
      { id: initialSortColumn, sortAscending: false }
    ];

    const styleConfig = {
      icons: {
        TableHeadingCell: {
          sortDescendingIcon: <FaCaretDown/>,
          sortAscendingIcon:  <FaCaretUp/>
        },
      },
      classNames: {
        Table: 'table', // table-hover table-outer-border
        NextButton: 'btn btn-sm btn-outline-dark next-page-button',
        PreviousButton: 'btn btn-sm btn-outline-dark previous-page-button',
        PageDropdown: 'btn-sm btn-outline-dark dropdown-toggle'
      },
      styles: {
        // Filter: { fontSize: 18 },
        // Table: { border: "2px solid #555 "},
      }
    }
    const pageProperties = {
      pageSize: props.pageSize || 10
    }
    // const events = {
    //   onSort: (sortProperties) => {
    //     // unselect previously sorted column header (if any)
    //     // $('.column-heading.sorted').className = "column-heading"
    //     // $(`#${sortProperties.id}-column-heading`).className = "column-heading sorted"
    //     console.log('onSort', sortProperties)
    //   }
    // };

    return (
        <div className='table-wrapper'>

            <Griddle
              data={dataStore.data}
              plugins={[plugins.LocalPlugin]}
              components={{Layout: getLayoutComponent(
                props.includeFilter, props.includePagination
              )}}
              styleConfig={styleConfig}
              sortProperties={sortProperties}
              pageProperties={pageProperties}
              //events={events}
              // resultsPerPage={5}
              // enableInfiniteScroll={true}
            >
              <RowDefinition>
                <ColumnDefinition id="county"
                  title={columnTitles['county']}
                  width={!!props.showAll ? 150: null}
                  headerCssClassName="county-header"
                  customHeadingComponent={CountyHeading}
                  customComponent={enhancedWithRowData(CountyColumn)} />
                <ColumnDefinition id="state"
                  title={columnTitles['state']}
                  customHeadingComponent={DefaultHeading}
                  customComponent={StateColumn} />
                <ColumnDefinition id="population"
                  title={columnTitles['population']}
                  cssClassName="right-align"
                  customHeadingComponent={DefaultHeading}/>

                <ColumnDefinition id="confirmed_last_14_per_100k"
                  visible={!props.aqOnly}
                  title={columnTitles['confirmed_last_14_per_100k']}
                  sortMethod={sortDescendingOnly}
                  headerCssClassName="left-border"
                  cssClassName="right-align no-padding-cell left-border"
                  customHeadingComponent={FourteenDayConfirmedHeading}
                  customComponent={FourteenDayConfirmedColumn} />
                <ColumnDefinition id="confirmed"
                  visible={!props.aqOnly}
                  title={columnTitles['confirmed']}
                  sortMethod={sortDescendingOnly}
                  cssClassName="right-align no-padding-cell"
                  customHeadingComponent={TotalCasesHeading}
                  customComponent={ConfirmedColumn} />
                <ColumnDefinition id="confirmed_difference"
                  title={columnTitles['confirmed_difference']}
                  sortMethod={sortDescendingOnly}
                  visible={!!props.showAll && !props.aqOnly}
                  cssClassName="right-align no-padding-cell"
                  customHeadingComponent={DefaultHeading}
                  customComponent={NewConfirmedColumn} />
                <ColumnDefinition id="deaths"
                  visible={!props.aqOnly}
                  title={columnTitles['deaths']}
                  sortMethod={sortDescendingOnly}
                  cssClassName="right-align no-padding-cell"
                  customHeadingComponent={DefaultHeading}
                  customComponent={DeathsColumn} />
                <ColumnDefinition id="deaths_difference"
                  title={columnTitles['deaths_difference']}
                  sortMethod={sortDescendingOnly}
                  visible={!!props.showAll && !props.aqOnly}
                  cssClassName="right-align no-padding-cell"
                  customHeadingComponent={DefaultHeading}
                  customComponent={NewDeathsColumn} />
                <ColumnDefinition id="recovered"
                  title={columnTitles['recovered']}
                  sortMethod={noSort}
                  visible={!!props.showAll && !props.aqOnly}
                  cssClassName="right-align"
                  customHeadingComponent={DefaultHeading} />
                <ColumnDefinition id="recovered_difference"
                  title={columnTitles['recovered_difference']}
                  sortMethod={noSort}
                  visible={!!props.showAll && !props.aqOnly}
                  cssClassName="right-align"
                  customHeadingComponent={DefaultHeading} />
                <ColumnDefinition id="active"
                  title={columnTitles['active']}
                  sortMethod={sortDescendingOnly}
                  visible={!!props.showAll && !props.aqOnly}
                  cssClassName="right-align no-padding-cell"
                  customHeadingComponent={DefaultHeading}
                  customComponent={ActiveColumn} />
                <ColumnDefinition id="active_difference"
                  title={columnTitles['active_difference']}
                  sortMethod={sortDescendingOnly}
                  visible={!!props.showAll && !props.aqOnly}
                  cssClassName="right-align no-padding-cell"
                  customHeadingComponent={DefaultHeading}
                  customComponent={NewActiveColumn} />
                <ColumnDefinition id="rate_confirmed"
                  title={columnTitles['rate_confirmed']}
                  sortMethod={sortDescendingOnly}
                  visible={!!props.showAll && !props.aqOnly}
                  cssClassName="right-align no-padding-cell"
                  customHeadingComponent={RateHeading}
                  customComponent={ConfirmedRateColumn}/>
                <ColumnDefinition id="five_day_doubling_rate"
                  title={columnTitles['five_day_doubling_rate']}
                  sortMethod={sortAscendingOnly}
                  visible={!!props.showAll && !props.aqOnly}
                  headerCssClassName="doubling-rate-header"
                  cssClassName="right-align no-padding-cell"
                  customHeadingComponent={DoublingRateHeading}
                  customComponent={DoublingRateColumn} />
                <ColumnDefinition id="rate_death"
                  title={columnTitles['rate_death']}
                  sortMethod={sortDescendingOnly}
                  visible={!!props.showAll && !props.aqOnly}
                  cssClassName="right-align no-padding-cell"
                  customHeadingComponent={RateHeading}
                  customComponent={DeathRateColumn} />
                <ColumnDefinition id="rate_recovered"
                  title={columnTitles['rate_recovered']}
                  sortMethod={noSort}
                  visible={!!props.showAll && !props.aqOnly}
                  cssClassName={'right-align'}
                  customHeadingComponent={RateHeading}/>
                <ColumnDefinition id="rate_active"
                  title={columnTitles['rate_active']}
                  sortMethod={sortDescendingOnly}
                  visible={!!props.showAll && !props.aqOnly}
                  cssClassName="right-align no-padding-cell"
                  customHeadingComponent={RateHeading}
                  customComponent={ActiveRateColumn} />

                <ColumnDefinition id="max_pm25"
                  title={columnTitles['max_pm25']}
                  sortMethod={sortDescendingOnly}
                  cssClassName="right-align left-border no-padding-cell"
                  headerCssClassName="left-border pm25-header"
                  customHeadingComponent={PM25GroupHeading}
                  customComponent={PM25Column}/>
                <ColumnDefinition id="mean_pm25"
                  title={columnTitles['mean_pm25']}
                  sortMethod={sortDescendingOnly}
                  cssClassName="right-align no-padding-cell"
                  headerCssClassName="pm25-header"
                  customHeadingComponent={PM25Heading}
                  customComponent={PM25Column} />
                <ColumnDefinition id="max_3hr_pm25_avg"
                  title={columnTitles['max_3hr_pm25_avg']}
                  sortMethod={sortDescendingOnly}
                  cssClassName="right-align no-padding-cell"
                  headerCssClassName="pm25-header"
                  customHeadingComponent={PM253HrHeading}
                  customComponent={PM25Column} />
                <ColumnDefinition id="min_pm25"
                  title={columnTitles['min_pm25']}
                  sortMethod={sortDescendingOnly}
                  visible={!!props.showAll}
                  cssClassName="right-align no-padding-cell"
                  headerCssClassName="pm25-header"
                  customHeadingComponent={PM25Heading}
                  customComponent={PM25Column} />

                <ColumnDefinition id="exposure"
                  visible={!props.aqOnly}
                  title={columnTitles['exposure']}
                  sortMethod={sortDescendingOnly}
                  cssClassName="right-align left-border no-padding-cell"
                  headerCssClassName="left-border"
                  customHeadingComponent={ExposureGroupHeading}
                  customComponent={ExposureColumn} />

              </RowDefinition>
            </Griddle>
        </div>
    )
})
